import { isNil } from 'lodash';

// CONSTANTS
import { DEFAULT_PER_PAGE, DEFAULT_OFFSET } from 'constants/table';
import { VEHICLE_OWNERS } from 'constants/vehicles';

// ACTIONS
import {
  receivedAddDamageFail,
  receivedAddDamageSuccess,
  receivedAddDamagePictureFail,
  receivedAddDamagePictureSuccess,
  receivedAddVehicleFail,
  receivedAddVehicleNoteFail,
  receivedAddVehicleNoteSuccess,
  receivedAddVehicleSuccess,
  receivedAdjustOdometerOffsetFail,
  receivedAdjustOdometerOffsetSuccess,
  receivedChangedVehiclesOwnerFail,
  receivedChangedVehiclesOwnerSuccess,
  receivedClearFilters,
  receivedClearVehicle,
  receivedClearVehicleErrors,
  receivedDepartmentsFail,
  receivedDepartmentsSuccess,
  receivedHistoryFail,
  receivedHistorySuccess,
  receivedNewFilter,
  receivedPerPageValue,
  receivedRefreshConnectedVehicleDataFail,
  receivedRefreshConnectedVehicleDataSuccess,
  receivedRefreshFuelSuccess,
  receivedRefreshOdometerSuccess,
  receivedRemoveDamageFail,
  receivedRemoveDamagePictureFail,
  receivedRemoveDamagePictureSuccess,
  receivedRemoveDamageSuccess,
  receivedRemoveFilter,
  receivedResetVehicles,
  receivedSelectedView,
  receivedSetActiveTab,
  receivedSetCalendarStart,
  receivedSetColumns,
  receivedSetDamagesFail,
  receivedSetDamagesSuccess,
  receivedSetSearchQuery,
  receivedSetVehicle,
  receivedStatusesFail,
  receivedStatusesSuccess,
  receivedUpdateDamageFail,
  receivedUpdateDamageSuccess,
  receivedUpdateVehicleDepartmentSuccess,
  receivedUpdateVehicleNoteFail,
  receivedUpdateVehicleNoteSuccess,
  receivedVehicleFail,
  receivedDashboardVehiclesFail,
  receivedDashboardVehiclesSuccess,
  receivedVehiclesFail,
  receivedVehiclesLightFail,
  receivedVehiclesLightSuccess,
  receivedVehiclesOffset,
  receivedVehiclesSuccess,
  receivedVehicleSuccess,
  startLoading,
  startLoadingDashboardVehicles,
  startLoadingLightVehicles,
  startLoadingVehicles,
  startRefreshFuelLoading,
  startRefreshOdometerLoading,
  updateVehicleInfoFail,
  updateVehicleInfoSuccess,
  startRefreshBatteryLoading,
  receivedRefreshBatterySuccess,
} from './actions';

// RESOURCES
import {
  addModel,
  addNote,
  changeOwner,
  create,
  createDamage,
  createPicture,
  deleteDamage,
  deletePicture,
  getConnectedVehicleData as getConnectedVehicleDataRequest,
  getDepartments,
  getHistory,
  getModels,
  getStatuses,
  partialUpdateModel,
  patchNote,
  query,
  queryLight,
  querySingleVehicle,
  requestAdjustOffset,
  setDamages as setDamagesRequest,
  update,
  updateDamage,
} from './resources';
import { FuelType } from 'services/resources';

// SERVICES
import { nextRequestId } from 'services/latestRequest';

//=================================================
//  SYNCHRONOUS THUNKS
//=================================================

export const clearVehicleErrors = () => (dispatch) => dispatch(receivedClearVehicleErrors());

/**
 * Selects a new active view
 */
export const selectView = (view) => (dispatch) => dispatch(receivedSelectedView(view));

/**
 * Adds a new filter to the store
 */
export const addNewFilter = (filter) => (dispatch) => dispatch(receivedNewFilter(filter));

/**
 * Removes a filter from the store
 */
export const removeFilter = (index) => (dispatch) => dispatch(receivedRemoveFilter(index));

/*
 * Clears any modifications to the filters
 */
export const clearFilters = () => (dispatch) => dispatch(receivedClearFilters());

/*
 * Sets the tab to display onMount
 */
export const setActiveTab = (tab) => (dispatch) => dispatch(receivedSetActiveTab(tab));

/*
 * Sets the calendar start date to display onMount
 */
export const setCalendarStart = (start) => (dispatch) => dispatch(receivedSetCalendarStart(start));

/**
 * Sets the columns to be displayed in the table
 */
export const setColumns = (columns) => (dispatch) => dispatch(receivedSetColumns(columns));

export const setSearchQuery = (searchQuery) => (dispatch) => dispatch(receivedSetSearchQuery(searchQuery));

/**
 * Resets the vehicles content. Used in cases where the sort is applied for example
 * but you want to stay on the same page with the same per page amount
 */
export const resetVehicles = () => (dispatch) => dispatch(receivedResetVehicles());

export const setVehicle = (vehicle) => (dispatch) => {
  dispatch(receivedSetVehicle(vehicle));

  return Promise.resolve();
};

/**
 * This in turn sets the active vehicles page. Used for when the
 * data for that page has already been loaded
 */
export const setVehiclesOffset = (offset) => (dispatch) => dispatch(receivedVehiclesOffset(offset));

//=================================================
//  ASYNC THUNKS
//=================================================

/**
 * Gets a list of vehicles
 */
export const getVehicles =
  (limit = DEFAULT_PER_PAGE, offset = DEFAULT_OFFSET, filters = new URLSearchParams(), searchQuery = '') =>
  (dispatch) => {
    const requestId = nextRequestId();

    dispatch(startLoadingVehicles(requestId));

    return query(limit, offset, filters, searchQuery)
      .then((response) => {
        dispatch(receivedVehiclesSuccess(response, offset, requestId));
        return response;
      })
      .catch((response) => {
        dispatch(receivedVehiclesFail(response.data, requestId));
      });
  };

/**
 * Gets a list of vehicles with limited information to be displayed on the map
 */
export const getMapVehicles =
  (filters = new URLSearchParams(), searchQuery = '', isLoading) =>
  (dispatch) => {
    if (isLoading === true) {
      dispatch(startLoadingLightVehicles());
    }

    return queryLight(filters, searchQuery)
      .then((response) => {
        dispatch(receivedVehiclesLightSuccess(response));
      })
      .catch((response) => {
        dispatch(receivedVehiclesLightFail(response.data));
      });
  };

/**
 * Gets a list of vehicles (fleet + defleeted) for the dashboard, keyed by VIN.
 */
export const getDashboardVehicles =
  (filters = new URLSearchParams()) =>
  (dispatch) => {
    dispatch(startLoadingDashboardVehicles());

    const params = new URLSearchParams({
      owner__in: `${VEHICLE_OWNERS.fleet},${VEHICLE_OWNERS.inventory}`,
    });
    for (const [key, val] of filters.entries()) {
      params.append(key, val);
    }

    return query(250, 0, params)
      .then((response) => {
        dispatch(receivedDashboardVehiclesSuccess(response));
        return response;
      })
      .catch((response) => {
        dispatch(receivedDashboardVehiclesFail(response.data));
      });
  };

/**
 * Changes the owner of the given vehicles
 */
export const changeVehiclesOwner = (vehicles, owner) => (dispatch) => {
  dispatch(startLoading());

  return changeOwner(vehicles, owner)
    .then(() => {
      dispatch(receivedChangedVehiclesOwnerSuccess(vehicles, owner));
    })
    .catch((response) => {
      dispatch(receivedChangedVehiclesOwnerFail(response.data));
    });
};

/**
 * Changes the per page value
 */
export const changePerPageValue = (perPage) => (dispatch) => dispatch(receivedPerPageValue(perPage));

/**
 * @deprecated
 * This thunk is deprecated and should not be used. Use the dealershipSettings `getDealershipDepartments` instead.
 * Gets department list
 */
export const getDepartmentsList = () => (dispatch) => {
  dispatch(startLoading());

  return getDepartments()
    .then((response) => {
      dispatch(receivedDepartmentsSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedDepartmentsFail(response.data));
    });
};

export const getStatusList = () => (dispatch) => {
  dispatch(startLoading());

  return getStatuses()
    .then((response) => {
      dispatch(receivedStatusesSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedStatusesFail(response.data));
    });
};

/**
 * Gets a vehicle info
 */
export const getVehicle = (id: number) => (dispatch) => {
  dispatch(startLoading());

  return querySingleVehicle(id)
    .then((response) => {
      dispatch(receivedVehicleSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedVehicleFail(response.data));
    });
};

export const clearVehicle = () => (dispatch) => {
  dispatch(receivedClearVehicle());
};

/**
 * Update or create a vehicle model
 */
export const setModel = (id: number, filters: object, newModelInfo: any) => (dispatch) =>
  getModels(filters)
    .then((response) => {
      // @ts-expect-error TS(2339): Property 'count' does not exist on type 'unknown'.
      if (response.count === 0) {
        return (
          addModel(newModelInfo)
            // @ts-expect-error TS(2339): Property 'id' does not exist on type 'unknown'.
            .then((addResponse) => update(id, { model: { id: addResponse.id } }))
            .then((updateResponse) => {
              dispatch(updateVehicleInfoSuccess(updateResponse));
            })
        );
      }

      // @ts-expect-error TS(2339): Property 'count' does not exist on type 'unknown'.
      if (response.count === 1) {
        return update(id, {
          // @ts-expect-error TS(2339): Property 'results' does not exist on type 'unknown'.
          model: { id: response.results[0].id },
        }).then((updateResponse) => {
          dispatch(updateVehicleInfoSuccess(updateResponse));
        });
      }

      return Promise.reject('Failure updating vehicle model');
    })
    .catch((response) => {
      dispatch(updateVehicleInfoFail(response.data));
      return Promise.reject(response);
    });

/**
 * Given a make, model and year, this function will find an existing model in the
 * database or create a new one and return it
 */
const findOrCreateModel = (
  make: string,
  model: string,
  year: string,
  fuelTankCapacity: number | null,
  fuelType: FuelType | null
) =>
  getModels({ make, model, year })
    .then((response) => {
      // @ts-expect-error TS(2339): Property 'count' does not exist on type 'unknown'.
      if (response.count === 0) {
        return null;
      }

      // @ts-expect-error TS(2339): Property 'results' does not exist on type 'unknown'.
      return response.results[0];
    })
    .then((existingModel) => {
      if (existingModel && fuelTankCapacity && isNil(existingModel.fuelTankCapacity)) {
        return partialUpdateModel(existingModel.id, { fuelTankCapacity });
      }

      if (!existingModel) {
        return addModel({
          make,
          model,
          year,
          fuelTankCapacity,
          fuelType,
        });
      }

      return existingModel;
    });

/**
 * Creates a new vehicle
 */
export const createVehicle =
  (
    vin: string,
    stockNumber: string,
    make: string,
    model: string,
    year: string,
    dealership: number,
    exteriorColor: string | undefined,
    fuelTankCapacity: number | undefined,
    fuelUnit: string,
    odometerUnit: string,
    fuelType: FuelType | null
  ) =>
  (dispatch) => {
    dispatch(startLoading());

    return findOrCreateModel(make, model, year, fuelTankCapacity, fuelType)
      .then((vehicleModel) => {
        create(vin, stockNumber, vehicleModel.id, dealership, exteriorColor, fuelUnit, odometerUnit, fuelType);
      })
      .then((response) => {
        dispatch(receivedAddVehicleSuccess(response));
        return Promise.resolve(response);
      })
      .catch((response) => {
        dispatch(receivedAddVehicleFail(response.data));

        return Promise.reject(response);
      });
  };

/**
 * Update a vehicle info
 */
export const updateVehicle = (id, newVehicleInfo) => (dispatch) =>
  update(id, newVehicleInfo)
    .then((response) => {
      dispatch(updateVehicleInfoSuccess(response));
      return Promise.resolve(response);
    })
    .catch((response) => {
      dispatch(updateVehicleInfoFail(response.data));
      return Promise.reject(response);
    });

export const addVehicleNote = (id, note) => (dispatch) =>
  addNote(id, note)
    .then((response) => {
      dispatch(receivedAddVehicleNoteSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedAddVehicleNoteFail(response.data));
    });

export const updateNote = (vehicleId, note) => (dispatch) =>
  patchNote(vehicleId, note)
    .then((response) => {
      dispatch(receivedUpdateVehicleNoteSuccess(response));
    })
    .catch((response) => {
      dispatch(receivedUpdateVehicleNoteFail(response.data));
    });

export const setDamages = (id, damages) => (dispatch) =>
  setDamagesRequest(id, damages)
    .then((response) => {
      dispatch(receivedSetDamagesSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedSetDamagesFail(response.data));
      return response;
    });

export const addDamagePicture = (vehicleId, damageId, pictures) => (dispatch) =>
  createPicture(vehicleId, damageId, pictures)
    .then((response) => {
      dispatch(receivedAddDamagePictureSuccess(damageId, response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedAddDamagePictureFail(response.data));
      return response;
    });

export const addDamage = (vehicleId, damage, pictures) => (dispatch) =>
  createDamage(vehicleId, damage)
    .then((response) => {
      dispatch(receivedAddDamageSuccess(response));
      if (pictures) {
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'unknown'.
        dispatch(addDamagePicture(vehicleId, response.id, pictures));
      }
      return response;
    })
    .catch((response) => {
      dispatch(receivedAddDamageFail(response.data));
      return response;
    });

export const removeDamage = (vehicleId, damageId) => (dispatch) =>
  deleteDamage(vehicleId, damageId)
    .then((response) => {
      dispatch(receivedRemoveDamageSuccess(damageId));
      return response;
    })
    .catch((response) => {
      dispatch(receivedRemoveDamageFail(response.data));
      return response;
    });

export const modifyDamage = (vehicleId, damage) => (dispatch) =>
  updateDamage(vehicleId, damage)
    .then((response) => {
      dispatch(receivedUpdateDamageSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedUpdateDamageFail(response.data));
      return response;
    });

export const removeDamagePicture = (vehicleId, damageId, pictureId) => (dispatch) =>
  deletePicture(vehicleId, damageId, pictureId)
    .then((response) => {
      dispatch(receivedRemoveDamagePictureSuccess(damageId, pictureId));
      return response;
    })
    .catch((response) => {
      dispatch(receivedRemoveDamagePictureFail(response.data));
      return response;
    });

/**
 * Gets a vehicle's history
 */
export const getVehicleHistory =
  (id, filter = null) =>
  (dispatch) => {
    const historyFilter = filter || '';

    return getHistory(id, historyFilter)
      .then((response) => {
        dispatch(receivedHistorySuccess(response));
        return Promise.resolve(response);
      })
      .catch((response) => {
        dispatch(receivedHistoryFail(response.data));
        return Promise.reject(response);
      });
  };

export const getConnectedVehicleData = (id) => (dispatch) => {
  dispatch(startRefreshFuelLoading());
  dispatch(startRefreshOdometerLoading());
  dispatch(startRefreshBatteryLoading());

  return getConnectedVehicleDataRequest(id)
    .then((response) => {
      dispatch(receivedRefreshConnectedVehicleDataSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedRefreshConnectedVehicleDataFail(response.data));
    });
};

export const refreshFuel = (id, fuelUnit) => (dispatch) => {
  dispatch(startRefreshFuelLoading());

  return update(id, { fuelLevel: null, fuelUnit })
    .then((response) => {
      dispatch(receivedRefreshFuelSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedVehicleFail(response.data));
    });
};

export const refreshBatteryLevel = (id) => (dispatch) => {
  dispatch(startRefreshBatteryLoading());

  return update(id, { batteryLevel: null })
    .then((response) => {
      dispatch(receivedRefreshBatterySuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedVehicleFail(response.data));
    });
};

export const refreshOdometer = (id, odometerUnit) => (dispatch) => {
  dispatch(startRefreshOdometerLoading());

  return update(id, { odometer: null, odometerUnit })
    .then((response) => {
      dispatch(receivedRefreshOdometerSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedVehicleFail(response.data));
    });
};

export const adjustOdometerOffset = (id, odometer) => (dispatch) => {
  dispatch(startLoading());

  return requestAdjustOffset(id, { odometer })
    .then((response) => {
      dispatch(receivedAdjustOdometerOffsetSuccess());
      return response;
    })
    .catch((response) => {
      dispatch(receivedAdjustOdometerOffsetFail(response.data));
      return Promise.reject();
    });
};

export const changeDepartment = (vehicleId, departmentId) => (dispatch) =>
  update(vehicleId, { department: { id: departmentId } })
    .then((response) => {
      dispatch(receivedUpdateVehicleDepartmentSuccess());
    })
    .catch((response) => {
      // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
      dispatch(updateVehicleInfoFail());
      return Promise.reject();
    });
